/**
 * UiScrollReducer.js
 *
 * Manages scroll position state for screen-based scroll restoration
 * Stores scroll offsets keyed by screenKey for session-based restoration
 * Data shape: { scrollPositions: { [screenKey]: { x, y, timestamp } } }
 */

// ============================================================================
// ACTION TYPES AND CREATORS
// ============================================================================

export const SAVE_SCROLL_POSITION = 'UI_SCROLL_SAVE';
export const RESTORE_SCROLL_POSITION = 'UI_SCROLL_RESTORE';
export const CLEAR_SCROLL_POSITION = 'UI_SCROLL_CLEAR_SCREEN';
export const CLEAR_ALL_SCROLL = 'UI_SCROLL_CLEAR_ALL';
export const CLEAR_SCROLL_POSITIONS_BY_PREFIX = 'UI_SCROLL_CLEAR_BY_PREFIX';

/**
 * Save scroll position for a screen
 * @param {string} screenKey - Unique identifier for the screen
 * @param {number} x - Horizontal scroll position
 * @param {number} y - Vertical scroll position
 */
export const saveScrollPosition = (screenKey, x, y) => ({
  type: SAVE_SCROLL_POSITION,
  payload: { screenKey, x, y },
});

/**
 * Restore scroll position for a screen
 * @param {string} screenKey - Unique identifier for the screen
 * @param {object} position - Position object with x and y coordinates
 */
export const restoreScrollPosition = (screenKey, position) => ({
  type: RESTORE_SCROLL_POSITION,
  payload: { screenKey, position },
});

/**
 * Clear scroll position for a specific screen
 * @param {string} screenKey - Unique identifier for the screen
 */
export const clearScrollPosition = (screenKey) => ({
  type: CLEAR_SCROLL_POSITION,
  payload: { screenKey },
});

/**
 * Clear all scroll positions (e.g., on logout)
 */
export const clearAllScrollPositions = () => ({
  type: CLEAR_ALL_SCROLL,
  payload: {},
});

/**
 * Clear all scroll positions whose key starts with the given prefix
 * @param {string} prefix - Key prefix to match (e.g., 'WorkflowStep_')
 */
export const clearScrollPositionsByPrefix = (prefix) => ({
  type: CLEAR_SCROLL_POSITIONS_BY_PREFIX,
  payload: { prefix },
});

// ============================================================================
// REDUCER
// ============================================================================

const INITIAL_STATE = {
  scrollPositions: {}, // { [screenKey]: { x: number, y: number, timestamp } }
};

const UiScrollReducer = (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    case 'UI_SCROLL_SAVE':
      return {
        ...state,
        scrollPositions: {
          ...state.scrollPositions,
          [payload.screenKey]: {
            x: payload.x,
            y: payload.y,
            timestamp: Date.now(),
          },
        },
      };

    case 'UI_SCROLL_RESTORE':
      return {
        ...state,
        scrollPositions: {
          ...state.scrollPositions,
          [payload.screenKey]: payload.position,
        },
      };

    case 'UI_SCROLL_CLEAR_SCREEN':
      const newPositions = { ...state.scrollPositions };
      delete newPositions[payload.screenKey];
      return {
        ...state,
        scrollPositions: newPositions,
      };

    case 'UI_SCROLL_CLEAR_ALL':
      return INITIAL_STATE;

    case 'UI_SCROLL_CLEAR_BY_PREFIX': {
      const newPositions = Object.fromEntries(
        Object.entries(state.scrollPositions).filter(([key]) => !key.startsWith(payload.prefix))
      );
      return { ...state, scrollPositions: newPositions };
    }

    default:
      return state;
  }
};

export default UiScrollReducer;
